import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { theme } from '../../../../../common/styles/theme';

// Draggable divider, VS Code style: three dots at rest, a blue line on hover/drag.
// 'column' sits between two columns and reports the pointer's X; 'row' sits between two rows and
// reports Y — the caller turns that into a size.
export const Splitter: React.FC<{
	orientation: 'column' | 'row';
	onDrag: (clientPos: number) => void;
	onDoubleClick?: () => void;
	sx?: SxProps<Theme>;
}> = ({ orientation, onDrag, onDoubleClick, sx }) => {

	const [dragging, setDragging] = React.useState(false);
	const isColumn = orientation === 'column';

	const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
		e.preventDefault();
		e.currentTarget.setPointerCapture(e.pointerId);
		setDragging(true);
	};

	const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
		if (dragging) onDrag(isColumn ? e.clientX : e.clientY);
	};

	const onPointerUp = (e: React.PointerEvent<HTMLElement>) => {
		e.currentTarget.releasePointerCapture(e.pointerId);
		setDragging(false);
	};

	return (
		<Box
			className={dragging ? 'dragging' : ''}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			onDoubleClick={onDoubleClick}
			sx={{
				position: 'relative',
				flex: `0 0 ${theme.spacing(1)}`,
				cursor: isColumn ? 'col-resize' : 'row-resize',
				touchAction: 'none',
				userSelect: 'none',

				// the line, shown on hover/drag
				'&:before': {
					content: '""',
					position: 'absolute',
					...(isColumn
						? { top: 0, bottom: 0, left: '50%', width: '4px', ml: '-2px' }
						: { left: 0, right: 0, top: '50%', height: '4px', mt: '-2px' }),
					background: theme.palette.primary.main,
					opacity: 0,
					transition: 'opacity .15s ease .1s',
				},
				// the three dots, shown at rest — 6px apart, so 18px fits the last one whole
				'&:after': {
					content: '""',
					position: 'absolute',
					top: '50%',
					left: '50%',
					...(isColumn
						? { width: '3px', height: '18px', m: '-9px 0 0 -1.5px', backgroundSize: '3px 6px' }
						: { width: '18px', height: '3px', m: '-1.5px 0 0 -9px', backgroundSize: '6px 3px' }),
					backgroundImage: `radial-gradient(circle, ${theme.palette.grey[400]} 1.5px, transparent 1.5px)`,
					transition: 'opacity .15s ease',
				},
				'&:hover, &.dragging': {
					'&:before': { opacity: 1 },
					'&:after': { opacity: 0 },
				},
				...(sx as object),
			}}
		/>
	);
};
